import React from 'react';
import StorySmall from '@moment/components/StorySmall/StorySmall.tsx';
import StoryMedium from '@moment/components/StoryMedium/StoryMedium.tsx';
import GameMedium from '@moment/components/GameMedium/GameMedium.tsx';
import ProductCard from '@moment/components/ProductCard/ProductCard.tsx';
import StoryCarousel from '@moment/components/StoryCarousel/StoryCarousel.tsx';
import StoryTop from '@moment/components/StoryTop/StoryTop.tsx';
import GamesTile from '@moment/components/GamesTile/GamesTile.tsx';
import ThirdPartyScript from '@moment/components/ThirdPartyScript/ThirdPartyScript.tsx';
import ThirdPartyEmbed from '@moment/components/ThirdPartyEmbed/ThirdPartyEmbed.tsx';
import { AdPlacementWidget } from './ad-widgets.tsx';
import { PropDefinition } from './widgets.tsx';

// These are placeholders with mock data for visual representation in the editor.
const StorySmallPlaceholder = () => (
  <div
    style={{
      pointerEvents: 'none',
      border: '1px dashed #ccc',
      padding: '0.5rem',
      borderRadius: '4px',
    }}
  >
    <StorySmall
      title="Small Story Placeholder"
      category="Category"
      imageList={[
        {
          url: 'https://via.placeholder.com/150x150/e0e0e0/9e9e9e?text=Small',
          width: 150,
          height: 150,
        },
      ]}
    />
  </div>
);

const StoryMediumPlaceholder = () => (
  <div
    style={{
      pointerEvents: 'none',
      border: '1px dashed #ccc',
      padding: '0.5rem',
      borderRadius: '4px',
    }}
  >
    <StoryMedium
      title="Medium Story Placeholder Title"
      description="This is a placeholder description for a medium-sized story component."
      tags={['tag1', 'tag2']}
      imageList={[
        {
          url: 'https://via.placeholder.com/300x200/e0e0e0/9e9e9e?text=Medium',
          width: 300,
          height: 200,
        },
      ]}
    />
  </div>
);

export type FragmentWidgetType =
  | 'StorySmall'
  | 'StoryMedium'
  | 'GameMedium'
  | 'ProductCard'
  | 'StoryCarousel'
  | 'StoryTop'
  | 'GamesTile'
  | 'AdPlacement'
  | 'ThirdPartyScript'
  | 'ThirdPartyEmbed';

// Define the structure for a property definition

export interface FragmentLayoutItem {
  instanceId: string;
  type: FragmentWidgetType;
  props?: Record<string, any>; // Add props support
}

export interface FragmentWidgetConfig {
  id: FragmentWidgetType;
  name: string;
  component: React.FC<any>;
  propsDefinition?: PropDefinition[]; // Add prop definitions
}

export const FRAGMENT_WIDGETS_CONFIG: FragmentWidgetConfig[] = [
  {
    id: 'StorySmall',
    name: 'Story Small',
    component: StorySmallPlaceholder,
    propsDefinition: [
      {
        name: 'hidePublisher',
        label: 'Hide Publisher?',
        type: 'boolean',
        defaultValue: false,
      },
      {
        name: 'hideCategory',
        label: 'Hide Category?',
        type: 'boolean',
        defaultValue: true,
      },
    ],
  },
  {
    id: 'StoryMedium',
    name: 'Story Medium',
    component: StoryMediumPlaceholder,
    propsDefinition: [
      {
        name: 'hidePublisher',
        label: 'Hide Publisher?',
        type: 'boolean',
        defaultValue: false,
      },
      {
        name: 'hideCategory',
        label: 'Hide Category?',
        type: 'boolean',
        defaultValue: true,
      },
    ],
  },
  {
    id: 'GameMedium',
    name: 'Game Medium',
    component: GameMedium,
  },
  {
    id: 'GamesTile',
    name: 'Games Tile',
    component: GamesTile,
  },
  {
    id: 'ProductCard',
    name: 'Product Card',
    component: ProductCard,
  },
  {
    id: 'StoryCarousel',
    name: 'Story Carousel',
    component: StoryCarousel,
  },
  {
    id: 'StoryTop',
    name: 'Story Top',
    component: StoryTop,
  },
  {
    id: 'ThirdPartyScript',
    name: 'Third-Party Script',
    component: ThirdPartyScript,
    propsDefinition: [
      {
        name: 'scriptSrc',
        type: 'string',
        label: 'Script URL or tag',
        defaultValue: '',
      },
      {
        name: 'async',
        type: 'boolean',
        label: 'Load async',
        defaultValue: true,
      },
    ],
  },
  {
    id: 'ThirdPartyEmbed',
    name: 'Third-Party Embed',
    component: ThirdPartyEmbed,
    propsDefinition: [
      {
        name: 'snippet',
        type: 'string',
        label: 'HTML embed snippet',
        defaultValue: '',
      },
      {
        name: 'async',
        type: 'boolean',
        label: 'Load async',
        defaultValue: true,
      },
    ],
  },
  {
    id: 'AdPlacement',
    name: 'Ad Placement',
    component: AdPlacementWidget,
    propsDefinition: [
      {
        name: 'placement',
        category: 'ad_placements',
        label: 'Ad Placement',
        type: 'dropdown',
        defaultValue: 'top',
      },
    ],
  },
];
